// Small builders for the record page definitions in this folder: fields,
// related-list columns and lookups, typed from @olytic/foundation. They only shape
// ObjectMetadata; ObjectRecordPage renders it.
import { BooleanMark, type ColumnDef, type FieldDef, type RecordLinkDef, type RelatedListDef, TextLink } from '@olytic/foundation';
import type { ReactNode } from 'react';

import type { ObjectKey } from '../../navigation';
import { formatDate, formatDateTime, formatMoney, formatNumber, formatPercent, humanize } from '../../objects/format';
import { resolveDef } from '../../objects/listState';
import type { ObjectDef } from '../../objects/objectDefs';
import type { MetaFieldType, ObjectRow } from '../../objects/objectsApi';
import { recordPath } from '../../objects/routes';

export type Row = ObjectRow;
type Format = (value: unknown) => string;

const blank = (value: unknown): boolean =>
  value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);

/** Any API value as text: arrays joined, JSON compact, blanks as an em dash. */
export function text(value: unknown): string {
  if (blank(value)) return '—';
  if (Array.isArray(value)) return value.map((v) => (typeof v === 'object' && v !== null ? label(v) : String(v))).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** A participant or similar object: its name, else its email, else JSON. */
function label(value: object): string {
  const v = value as Record<string, unknown>;
  if (typeof v.name === 'string' && v.name !== '') return v.name;
  if (typeof v.email === 'string' && v.email !== '') return v.email;
  return JSON.stringify(value);
}

/** Formatters that show an em dash for a blank. */
export const show = (format: Format): Format => (value) => (blank(value) ? '—' : format(value) || '—');
export const asDate = show(formatDate);
export const asDateTime = show(formatDateTime);
/** A yes/no field as the library's checkbox mark (ticked or empty); blank as an em dash. */
export const asCheck = (value: unknown): ReactNode =>
  blank(value) ? '—' : <BooleanMark value={value === true || value === 'true'} />;
export const asLabel = show(humanize);

/** A lookup to a parent: show `<x>_name`, link through `<x>_id`. */
export const lookup = (object: ObjectKey, idKey: string): RecordLinkDef => ({ object, idKey });

interface FieldOptions {
  format?: (value: unknown) => ReactNode;
  link?: RecordLinkDef;
  wide?: boolean;
  /** What the field means, shown when its label is hovered or focused. */
  help?: string;
}

/** A Details field. */
export function field(key: string, labelText: string, { format, link, wide, help }: FieldOptions = {}): FieldDef<Row> {
  return {
    key,
    label: labelText,
    render: (row) => (format ? format(row[key]) : text(row[key])),
    ...(link ? { link } : {}),
    ...(wide ? { wide: true } : {}),
    ...(help ? { help } : {}),
  };
}

/** Created By (migration 0107): who inserted the record, stamped by the database. */
export const createdByField = () => field('created_by_name', 'Created by', { link: lookup('people', 'created_by_id') });
/** Last Modified By (migration 0107): who last inserted or updated the record, stamped by the database. */
export const lastModifiedByField = () => field('last_modified_by_name', 'Last Modified by', { link: lookup('people', 'last_modified_by_id') });

/** How a Details field of each type in /meta shows its value, for a field that names no format. */
export const DETAIL_FORMATS: Record<MetaFieldType, (value: unknown) => ReactNode> = {
  text,
  picklist: asLabel,
  number: show(formatNumber),
  currency: show(formatMoney),
  percent: show(formatPercent),
  date: asDate,
  datetime: asDateTime,
  boolean: asCheck,
};

/**
 * The record page of an object described by the schema (the server's
 * SCHEMA_OBJECTS): which of its Details fields show by their type, and which
 * of the table's columns stay off the page. Every other column appears in
 * Other Fields (ObjectRecordView withSchemaDetails), so a new one needs no code.
 */
export interface SchemaRecord {
  typed: ReadonlySet<string>;
  hidden: ReadonlySet<string>;
}
export const SCHEMA_RECORDS = new Map<ObjectKey, SchemaRecord>();

/**
 * Starts an object's schema record page; the returned builder makes its
 * Details fields. A field with no format or link of its own is shown by its
 * type and, when it is a lookup, links to the related record.
 */
export function schemaRecord(object: ObjectKey, { hidden = [] }: { hidden?: string[] } = {}) {
  const typed = new Set<string>();
  SCHEMA_RECORDS.set(object, { typed, hidden: new Set(hidden) });
  return (key: string, labelText: string, options: FieldOptions = {}): FieldDef<Row> => {
    if (!options.format && !options.link) typed.add(key);
    return field(key, labelText, options);
  };
}

/** An object's list columns labelled by its Details fields (an object described by the schema labels them so). */
export function listColumnsOf(def: ObjectDef, details: FieldDef<Row>[]): ColumnDef<Row>[] {
  return resolveDef(def, null, new Map(details.map((d) => [d.key, typeof d.label === 'string' ? d.label : d.key]))).columns;
}

/** A Details field holding an external web address, shown as a link that opens a new tab (http and https only). */
export function urlField(key: string, labelText: string): FieldDef<Row> {
  return {
    key,
    label: labelText,
    render: (row) => {
      const value = row[key];
      if (typeof value !== 'string' || !/^https?:\/\//i.test(value)) return text(value);
      return (
        <TextLink href={value} target="_blank" rel="noreferrer noopener">
          {value}
        </TextLink>
      );
    },
  };
}

interface ColumnOptions {
  format?: Format;
  link?: RecordLinkDef;
  primary?: boolean;
  align?: 'left' | 'right';
}

/** A related-list column. The first column of a list is its name (`primary`). */
export function column(key: string, labelText: string, { format, link, primary, align }: ColumnOptions = {}): ColumnDef<Row> {
  return {
    key,
    label: labelText,
    // A name column keeps the library's link styling only without a custom render, so it formats through `get`.
    ...(format ? (primary ? { get: (row: Row) => format(row[key]) } : { render: (row: Row) => format(row[key]) }) : {}),
    ...(link ? { link } : {}),
    ...(primary ? { primary: true } : {}),
    ...(align ? { align, nowrap: true } : {}),
  };
}

/**
 * The name column of a list whose rows are records of different objects
 * (each row's `object` names it), so the link is built per row.
 */
export function mixedNameColumn(labelText: string): ColumnDef<Row> {
  return {
    key: 'name',
    label: labelText,
    primary: true,
    href: (row) => (typeof row.object === 'string' && row.object !== '' ? recordPath(row.object as ObjectKey, row.id) : null),
  };
}

/** A related list: `object` makes each row's name link to its record. */
export function related(
  key: string,
  labelText: string,
  object: ObjectKey | undefined,
  columns: ColumnDef<Row>[],
): RelatedListDef<Row> {
  return { key, label: labelText, ...(object ? { object } : {}), columns, previewCount: 5 };
}

/** Columns shared by every list of activities linked to a record. */
export const activityColumns = (): ColumnDef<Row>[] => [
  { ...column('subject', 'Subject', { primary: true }), get: (row) => text(row.subject ?? humanize(row.channel)) },
  column('channel', 'Channel', { format: asLabel }),
  column('occurred_at', 'When', { format: asDate }),
  column('owner_name', 'Owner'),
];

/** Columns shared by every list of documents linked to a record. */
export const documentColumns = (): ColumnDef<Row>[] => [
  column('name', 'Name', { primary: true }),
  column('mime_type', 'Type'),
  column('uploaded_at', 'Uploaded', { format: asDate }),
  column('owner_name', 'Owner'),
];

/** Columns of the audiences a contact or account belongs to. */
export const membershipColumns = (): ColumnDef<Row>[] => [
  column('name', 'Audience', { primary: true }),
  column('membership_type', 'Membership', { format: asLabel }),
  column('added_at', 'Added', { format: asDate }),
  column('added_reason', 'Reason', { format: asLabel }),
];

/**
 * Columns of the meetings linked to a record (migration 0012's meeting_*
 * tables), for a tab, where the list has the page's width. A rail list picks
 * the name, the date and one more (`railMeetingColumns`).
 */
export const meetingColumns = (): ColumnDef<Row>[] => [
  column('name', 'Meeting Name', { primary: true }),
  column('meeting_date', 'Date', { format: asDate }),
  column('meeting_category', 'Category', { format: asLabel }),
  column('summary_line', 'Summary'),
  column('source', 'Linked by', { format: asLabel }),
];

/** Three short meeting columns for the rail: name, date and `third`. */
export const railMeetingColumns = (third: ColumnDef<Row>): ColumnDef<Row>[] => [
  ...meetingColumns().slice(0, 2),
  third,
];

/** How a meeting link was made: calendar, transcript, manual or backfill. */
export const linkedByColumn = (): ColumnDef<Row> => column('source', 'Linked by', { format: asLabel });

/** Columns of the records an activity or document is linked to. */
export const linkedRecordColumns = (): ColumnDef<Row>[] => [
  mixedNameColumn('Record'),
  column('entity_type', 'Type', { format: asLabel }),
];

/** Columns of a record's resource assignments (0034): what, who, when and how much. */
export const assignmentColumns = (): ColumnDef<Row>[] => [
  column('name', 'Assignment', { primary: true }),
  column('app_user_name', 'Person', { link: lookup('people', 'app_user_id') }),
  column('hours', 'Hours', { format: show((v) => `${String(v)}h`), align: 'right' }),
  column('status', 'Status', { format: asLabel }),
];
